import type { Event } from "../../pojo/Event";
import type { Contact } from "../../pojo/Contact";
import type { Requirement } from "./Event";
import type { Proto } from "../../pojo/Proto";
import type { Nullable } from "../../pojo/Util";
import type { S25Reservation, S25RmReservation, S25RsReservation } from "./ReservationI";
import type { S25Profile, OccurrenceDef } from "./ProfileI";
import type { Item } from "../../pojo/Item";
import type { DropDownItem } from "../../pojo/DropDownItem";
import type { S25ItemI } from "../../pojo/S25ItemI";
import type { ContactRole } from "../object-details/event/ev.editable.contacts.component";
import type { HeadCount } from "../object-details/event/ev.editable.headcount.component";
import type { ProfileComments } from "../object-details/s25.object.details.editable.text.component";
import type { AttachmentItem } from "../../services/attachment.service";
type ISOTimeString = Proto.ISOTimeString;
type ISODateString = Proto.ISODateString;
type NumericalString = Proto.NumericalString;

export interface WsDataI {
    data: {
        items: EventMicroI[];
        id?: number;
        updated?: Date;
    };
    expandedInfo?: ExpandedInfoI;
    approval?: Event.Workflow.Task; // service call to get this info EventService.getEventsInclude(eventId, ["workflow"])
}

export interface EventListMicroI {
    kind?: "event" | "folder" | "cabinet" | "draft" | "unknown";
    id?: number;
    etag?: string;
    eventName?: string;
    eventTitle?: string;
    eventLocator?: string;
    updated?: ISODateString;
    dates?: { startDate?: ISODateString; endDate?: ISODateString };
}

export interface EventListWsDataI {
    data: {
        items: EventListMicroI[];
        paginateKey?: string;
        pageIndex?: number;
        totalPages?: number;
        totalItems?: number;
        currentItemCount?: number;
        itemsPerPage?: number;
    };
}

export interface EventMicroI {
    attributes?: Attribute[];
    categories?: Category[];
    context?: Context;
    dates?: { startDate: ISODateString; endDate: ISODateString };
    etag?: string;
    eventLocator?: string;
    externalId?: string;
    id?: number; //event ID
    isActive?: boolean;
    kind?: string;
    name?: string; //event name
    organizations?: {
        ancillary?: Organization[];
        primary?: number;
    };
    requirements?: Requirement[];
    roles?: Role[];
    priority?: number;
    profiles?: Profile[];
    text?: TextData[];
    title?: string;
    [key: string]: any; //allow any other properties
}

export interface ExpandedInfoI {
    attributes?: Attribute[];
    categories?: Category[];
    contacts?: Contact.Data[];
    eventTypes?: EventType[];
    organizations?: Organization[];
    parentNodes?: ParentNode[];
    requirements?: Requirement[];
    resources?: Resource[];
    roles?: Role[];
    spaces?: Space[];
    states?: State[];
    profiles?: Profile[];
    [key: string]: any; //allow any other properties
}

export interface Profile {
    comments?: string;
    expectedCount?: number | null;
    name?: string;
    occurrenceDefn?: OccurrenceDef;
    profileId?: number;
    registeredCount?: number | null;
    reservations?: (S25Reservation | WSReservations)[];

    [key: string]: any; //allow any other properties
}

export interface TextData {
    confirmation?: string;
    description?: string;
    note?: string;
}

export interface Attribute {
    attributeId?: number;
    value?: "F" | "T";
    attributeName?: string;
    dataType?: string;
    sort_order?: number;

    [key: string]: any; //allow any other properties
}

export interface Category {
    categoryId?: number;
    inheritCode?: number;
    categoryName?: string;
    sort_order?: number;

    [key: string]: any; //allow any other properties
}

export interface Context {
    parentId?: number;
    state?: Event.State.Ids; // event state id
    typeId?: number; //event type Id

    [key: string]: any; //allow any other properties
}

export interface Organization {
    primary?: number;
    etag?: number;
    organizationId?: number;
    organizationName?: string;
    organizationTitle?: string;
    organizationTypeId?: number;

    [key: string]: any; //allow any other properties
}

export interface ParentNode {
    endDt?: string;
    id?: number;
    locator?: string;
    name?: string;
    nodeType?: string;
    startDt?: string;
    typeName?: string;

    [key: string]: any; //allow any other properties
}

export interface Resource {
    etag?: string;
    resourceId: number;
    resourceName?: string;
    currentStockLevel?: number;
    stock?: Stock[];
    [key: string]: any; //allow any other properties
}

export interface Role {
    etag?: string;
    roleId: number;
    roleName?: string;
    contactId?: number;
    [key: string]: any; //allow any other properties
}

export interface Requirements {
    etag?: string;
    requirementId: number;
    requirementName?: string;
    requirementType?: number;
    sort_order?: number;
    allowComment?: boolean;
    comments?: string;
    quantity?: number;
    [key: string]: any; //allow any other properties
}

export interface Space {
    etag?: string;
    spaceId: number;
    spaceName?: string;
    maxCapacity?: number;
    spaceFormalName?: string;
}

export interface State {
    stateId: Event.State.Ids;
    stateName?: Event.State.Name;

    [key: string]: any; //allow any other properties
}

export interface EventType {
    typeId: number;
    typeName?: string;

    [key: string]: any; //allow any other properties
}
export interface EventDataMicroI {
    items: EventMicroI[];
}

export type WSReservations = {
    rsrvId?: number;
    state?: number;
    rsrvStartDt?: string | ISODateString | Date;
    evStartDt?: string | ISODateString | Date;
    evEndDt?: string | ISODateString | Date;
    rsrvEndDt?: string | ISODateString | Date;
    comments?: string;
    spaces?: S25RmReservation[];
    resources?: S25RsReservation[];
    remove?: any;
    occurrence?: string | null;
};

export type S25Event = {
    itemId: number;
    itemName: string;
    etag?: string;
    state?: number;
    profile?: S25Profile[];
    expandedInfo?: ExpandedInfoI;
};

export namespace S25Event {
    export function getLocator(str: Nullable<string>): string | null {
        if (!str) return null;
        if (isLocator(str)) return str;
        if (isPartialLocator(str)) return new Date().getFullYear() + "-" + str;
        return null;
    }

    export function isPartialLocator(str: string): boolean {
        return !!str && !!str.match(/^[Aa][A-Za-z]{5}$/i);
    }

    export function isLocator(str: Nullable<string>): boolean {
        return !!str && !!str.trim().match(/^\d{4}-[A-Za-z]{6}$/i);
    }
}

export type Stock = {
    versionNumber?: number;
    startDate?: Date;
    endDate?: Date;
    stockLevel?: number;
};

export type PutEventMircoListResponse = {
    id: number;
    requestId: number;
    updated: ISODateString;
    data?: {};
    messages?: {
        errorNum: string;
        objectType: Item.Id;
        objectId: number;
        reason: string;
        message: string;
    }[];
    error?: {
        kind: string;
        code: NumericalString;
        errorNum: string;
        reason: string;
        message: string;
        errors?: PutEventMicroListResponseError[];
        messages?: PutEventMicroListResponseError[];
    };
};

export type PutEventMicroListResponseError = {
    errorNum: string;
    objectType: Item.Id;
    objectId: number;
    objectName?: string;
    reason: string;
    message: string;
    subObjectId?: number;
    subObjectName?: string;
    occurrenceDate?: ISODateString;
    occurrenceTime?: ISOTimeString;
};

export type EventDetailData = {
    general: {
        name: string;
        title: string;
        eventType: DropDownItem;
        primaryOrg: DropDownItem;
        addtlOrgs: S25ItemI[];
        contactRoles: { assignedRoles: ContactRole[]; eventTypeRoles: Role[] };
        headCount: HeadCount[];
        text: {
            description: string;
            comments: ProfileComments[];
            note: string;
            confirmation: string;
        };
        attachments: {
            files: AttachmentItem[];
            itemTypeId: number;
            itemId: number;
            addAction: (file: any) => Promise<void>;
            removeAction: (file: any) => Promise<void>;
        };
    };
};
